"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.assertUpdateSuccessful = assertUpdateSuccessful;
const legacy_enums_1 = require("@daytrip/legacy-enums");
/**
 * Asserts that a MongoDB update operation was successful.
 *
 * This utility handles version-based optimistic locking validation for MongoDB updates.
 * It should be used after any update operation that uses version checking (e.g., `{ _id, version }`).
 *
 * @param result - The UpdateResult from a MongoDB update operation
 * @param hooks - Optional callbacks for handling specific error conditions
 * @param hooks.onInvalidVersion - Called before throwing InvalidVersion error (no document matched)
 * @param hooks.onCantProvideOperation - Called before throwing CantProvideOperation error (partial update)
 *
 * @throws {Error} RejectionType.InvalidVersion - When no document matched the query
 *                 (matchedCount === 0 && modifiedCount === 0), indicating a version mismatch
 * @throws {Error} RejectionType.CantProvideOperation - When the matched count doesn't equal
 *                 the modified count, indicating a partial or failed update
 *
 * @example
 * ```typescript
 * const result = await this.model.updateOne(
 *     { _id: orderId, version: order.version - 1 },
 *     { $set: order }
 * );
 *
 * assertUpdateSuccessful(result, {
 *     onInvalidVersion: () => {
 *         this.logger.warn({ message: "Version mismatch", payload: { orderId } });
 *     },
 *     onCantProvideOperation: () => {
 *         this.logger.info({ message: "Not updated", payload: { orderId, order, result } });
 *     },
 * });
 * ```
 */
function assertUpdateSuccessful(result, hooks = {}) {
    const { onCantProvideOperation = () => { }, onInvalidVersion = () => { } } = hooks;
    if (result.matchedCount === 0 && result.modifiedCount === 0) {
        onInvalidVersion();
        throw new Error(legacy_enums_1.RejectionType.InvalidVersion);
    }
    if (result.matchedCount !== result.modifiedCount) {
        onCantProvideOperation();
        throw new Error(legacy_enums_1.RejectionType.CantProvideOperation);
    }
}
//# sourceMappingURL=assert-update-successful.js.map